import { useState, useMemo, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, AlertCircle, Eye, EyeOff, ArrowRight, ShieldCheck } from 'lucide-react';
import { UserContext } from '../UserContext';
import { API_BASE_URL } from '../config/api';
import './Signup.css';
import './ChangePassword.css';

/* First-run "choose your password" screen.

   Reached immediately after a user signs in with the temporary password that
   was emailed to them. That password is not private, it travelled in
   plaintext, so the backend refuses every other authenticated route until it
   is replaced (see get_current_user in backend/src/app/api/dependencies.py).
   This screen is the only way through.

   Deliberately NOT the account-settings password form: this is a welcome step,
   not a maintenance task. It is framed as finishing setup, has no sidebar or
   navigation to wander off into, and offers no "skip", the only exits are
   completing the form or signing out.

   The strength meter is intentionally the same design as the one in
   components/account/PasswordSection.js so the two password forms behave
   identically; see that file for why a meter beats a requirements checklist. */

const MIN_LENGTH = 8;

const scorePassword = (value) => {
  if (!value) return 0;
  if (value.length < MIN_LENGTH) return 1;

  let score = 1;
  if (value.length >= 12) score += 1;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
  if (/[0-9]/.test(value)) score += 1;
  if (/[^a-zA-Z0-9]/.test(value)) score += 1;

  score = Math.min(score, 4);
  // Variety alone cannot reach the top rung: a short password with every
  // character class would otherwise read "Strong" while the hint still asks
  // for more length, which contradicts itself.
  if (value.length < 12) score = Math.min(score, 3);

  return score;
};

const LEVELS = [
  { key: 'empty', label: '' },
  { key: 'weak', label: 'Weak' },
  { key: 'fair', label: 'Fair' },
  { key: 'good', label: 'Good' },
  { key: 'strong', label: 'Strong' }
];

// One hint at a time, in the order that most improves the score.
const hintFor = (value) => {
  if (!value) return '';
  if (value.length < MIN_LENGTH) {
    const remaining = MIN_LENGTH - value.length;
    return `Add ${remaining} more character${remaining === 1 ? '' : 's'}.`;
  }
  if (value.length < 12) return 'Longer passwords are harder to guess.';
  if (!(/[a-z]/.test(value) && /[A-Z]/.test(value))) return 'Mix upper and lower case to strengthen it.';
  if (!/[0-9]/.test(value)) return 'Add a number to strengthen it.';
  if (!/[^a-zA-Z0-9]/.test(value)) return 'Add a symbol to strengthen it.';
  return 'This is a strong password.';
};

const EMPTY_FORM = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

const ChangePassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [revealed, setRevealed] = useState({});

  const navigate = useNavigate();
  const { logout, setMustChangePassword, userEmail } = useContext(UserContext);

  useEffect(() => {
    document.body.classList.add('nx-onboard-body');
    return () => document.body.classList.remove('nx-onboard-body');
  }, []);

  const score = useMemo(() => scorePassword(formData.newPassword), [formData.newPassword]);
  const hint = useMemo(
    () => (score === 4 ? 'This is a strong password.' : hintFor(formData.newPassword)),
    [formData.newPassword, score]
  );
  const level = LEVELS[score];

  const meetsFloor =
    formData.newPassword.length >= MIN_LENGTH &&
    /[a-zA-Z]/.test(formData.newPassword) &&
    /[0-9]/.test(formData.newPassword);

  const matches =
    formData.newPassword.length > 0 &&
    formData.newPassword === formData.confirmPassword;

  // Reusing the temporary password would clear the flag while leaving the
  // emailed credential live. The backend rejects this too; blocking it here
  // just gives a clearer message than a 400.
  const isReuse =
    formData.newPassword.length > 0 &&
    formData.newPassword === formData.currentPassword;

  const canSubmit =
    formData.currentPassword.length > 0 && meetsFloor && matches && !isReuse && !loading;

  const showMismatch =
    formData.confirmPassword.length > 0 &&
    formData.newPassword !== formData.confirmPassword;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const toggleReveal = (field) =>
    setRevealed((prev) => ({ ...prev, [field]: !prev[field] }));

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setFormData(EMPTY_FORM);
        setRevealed({});

        // The backend rotates the Activepieces token on password change, so the
        // stale one must be replaced rather than left behind.
        localStorage.removeItem('ap_token');
        localStorage.removeItem('projectId');
        if (data.ap_token) {
          localStorage.setItem('ap_token', data.ap_token);
          localStorage.setItem('projectId', data.ap_project_id);
        }

        // Lift the client-side gate so ProtectedRoute stops redirecting here.
        setMustChangePassword(false);
        navigate('/dashboard', { replace: true });
      } else {
        const detail = data.detail;
        setError(
          (typeof detail === 'string' ? detail : detail?.message) ||
            'We could not update your password. Please try again.'
        );
      }
    } catch (err) {
      setError('Something went wrong while saving your password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordField = ({ name, label, autoComplete, hintText }) => (
    <div className="nx-cp-field">
      <label htmlFor={`cp-${name}`}>{label}</label>
      <div className="nx-cp-input-wrap">
        <input
          id={`cp-${name}`}
          type={revealed[name] ? 'text' : 'password'}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          autoComplete={autoComplete}
          className={name === 'confirmPassword' && showMismatch ? 'nx-cp-input-error' : ''}
          aria-invalid={name === 'confirmPassword' && showMismatch ? 'true' : undefined}
          aria-describedby={hintText ? `cp-${name}-hint` : undefined}
          required
        />
        <button
          type="button"
          className="nx-cp-reveal"
          onClick={() => toggleReveal(name)}
          // Names the action and the field: unambiguous with three on one form.
          aria-label={`${revealed[name] ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
        >
          {revealed[name] ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {hintText && (
        <span className="nx-cp-field-hint" id={`cp-${name}-hint`}>
          {hintText}
        </span>
      )}
    </div>
  );

  return (
    <div className="nx-onboard-page nx-cp-page">
      <div className="nx-topbar">
        <div className="nx-brand">
          <div className="nx-brand-mark">
            <img className="nx-brand-logo" src="/images/NexOpta_Icon.png" alt="Nexopta" />
          </div>
          <div className="nx-brand-word">NEXOPTA</div>
        </div>
        <button type="button" className="nx-cp-logout" onClick={handleLogout}>
          Sign out
        </button>
      </div>

      <div className="nx-cp-content">
        <div className="nx-cp-card">
          <div className="nx-cp-header">
            <div className="nx-cp-badge" aria-hidden="true">
              <ShieldCheck size={22} />
            </div>
            <h1 className="nx-cp-title">One last step</h1>
            <p className="nx-cp-lead">
              You're signed in with the temporary password we emailed
              {userEmail ? <> to <strong>{userEmail}</strong></> : ''}. Because it
              was sent by email, it isn't private, choose your own password to
              finish securing your account.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="nx-cp-form">
            {error && (
              <div className="nx-cp-banner error" role="alert">
                <AlertCircle size={17} aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            {passwordField({
              name: 'currentPassword',
              label: 'Temporary password',
              autoComplete: 'current-password',
              hintText: 'The one from your welcome email.'
            })}

            <div className="nx-cp-divider" role="presentation" />

            {passwordField({
              name: 'newPassword',
              label: 'New password',
              autoComplete: 'new-password'
            })}

            {/* Four discrete segments rather than one continuous bar: steps
                make progress legible without implying false precision. */}
            <div className={`nx-cp-strength is-${level.key}`}>
              <div
                className="nx-cp-strength-track"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={4}
                aria-valuenow={score}
                aria-valuetext={
                  formData.newPassword
                    ? `Password strength: ${level.label}`
                    : 'No password entered'
                }
              >
                {[1, 2, 3, 4].map((step) => (
                  <span
                    key={step}
                    className={`nx-cp-strength-seg${score >= step ? ' filled' : ''}`}
                  />
                ))}
              </div>
              {/* aria-live on the hint only, announcing the bar too would
                  double up on every keystroke. */}
              <p className="nx-cp-strength-meta" aria-live="polite">
                <span className="nx-cp-strength-label">{level.label}</span>
                <span className="nx-cp-strength-hint">{hint}</span>
              </p>
            </div>

            {isReuse && (
              <span className="nx-cp-error-line">
                <AlertCircle size={13} aria-hidden="true" />
                Choose a password different from the temporary one.
              </span>
            )}

            {passwordField({
              name: 'confirmPassword',
              label: 'Confirm new password',
              autoComplete: 'new-password'
            })}

            {showMismatch && (
              <span className="nx-cp-error-line">
                <AlertCircle size={13} aria-hidden="true" />
                Passwords do not match.
              </span>
            )}

            <button type="submit" className="nx-cp-submit" disabled={!canSubmit}>
              {loading ? (
                <>
                  <span className="nx-cp-spinner" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                <>
                  Save and continue
                  <ArrowRight size={18} />
                </>
              )}
            </button>

            <p className="nx-cp-footnote">
              <Check size={14} aria-hidden="true" />
              <span>
                Once saved, the temporary password stops working immediately.
              </span>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
